/**
 * What a phone gets instead of a boot.
 *
 * Most invitations are read on a phone, and the course needs a computer: a
 * real Linux machine in the tab, a keyboard, and memory a phone browser does
 * not give a page. Before this, a phone downloaded the machine anyway and
 * either failed after a long wait or got a terminal nobody could type into
 * (in the last days of September, 6 of 15 visitors were phones and 2 of them
 * got a machine). So the useful thing on a phone is to carry the link to a
 * computer: the share sheet (mail, chat, notes, whatever the person uses),
 * with copying the link beside it, and a way through for the tablet with a
 * keyboard that can in fact run it.
 *
 * Since 2026-10-10 the way through is the first offer, not the last. The
 * owner does not want to lose the people who have no computer, and the play
 * screen now has a phone layout (PlayFrame's panels, PhoneKeys, a frame sized
 * above the keyboard). Phones that tried before it: 17 boots, 5 machines,
 * none cleared a level. So the page still says plainly what to expect and
 * still carries the link to a computer, second. A phone that chose to start
 * is remembered in localStorage and goes straight in next time.
 *
 * There is deliberately no "email me the link" form. A form that makes this
 * domain send mail to any address typed into it is a way to make us send
 * mail nobody asked for, and the share sheet does the same job from the
 * person's own account.
 *
 * Counted as a `boot-fail` with the word `phone`, like `unsupported` and
 * `memory`: a refusal before anything downloads is still a learner who did
 * not get a machine, and the funnel's browser table is where that is read.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { bootFailed } from '../lib/events';
import { useStrings } from '../state/useLocale';
import { Button, Mark } from '../ui';

const TRIED = 'eos-phone-try';

/** The same test `browserLabel` uses for `mobile` (lib/events.ts). */
export function isPhone(): boolean {
  try {
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  } catch {
    return false;
  }
}

/** Whether this phone has already chosen to start here; kept, so it is asked once rather than every visit. */
function triedAnyway(): boolean {
  try {
    return localStorage.getItem(TRIED) === '1';
  } catch {
    return false;
  }
}

/** The course, or the phone page in front of it. */
export function CourseDoor({ children }: { children: ReactNode }) {
  const [gated, setGated] = useState(() => isPhone() && !triedAnyway());
  if (!gated) return <>{children}</>;
  return (
    <PhoneGate
      onTryAnyway={() => {
        try {
          localStorage.setItem(TRIED, '1');
        } catch {
          /* The choice then lasts until a reload, which is fine. */
        }
        setGated(false);
      }}
    />
  );
}

function PhoneGate({ onTryAnyway }: { onTryAnyway(): void }) {
  const s = useStrings().boot.phone;
  const [copied, setCopied] = useState(false);
  const url = window.location.href;

  useEffect(() => {
    bootFailed('phone');
  }, []);

  const share = async () => {
    const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
    if (typeof nav.share === 'function') {
      try {
        await nav.share({ title: 'EngineerOS', text: s.shareText, url });
        return;
      } catch (err) {
        // Closing the sheet is an answer, not a failure.
        if ((err as { name?: string } | null)?.name === 'AbortError') return;
      }
    }
    // No share sheet: the mail app, addressed to nobody, so they write in their own.
    window.location.href = `mailto:?subject=${encodeURIComponent(s.shareText)}&body=${encodeURIComponent(url)}`;
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="boot">
      <div className="boot-inner">
        <p className="boot-brand">
          <Mark tile />
          <span className="brand-word">
            Engineer<b>OS</b>
          </span>
        </p>
        <h1 className="rail-title">{s.title}</h1>
        <p className="lede">{s.body}</p>
        <p className="boot-actions">
          <Button variant="solid" onClick={onTryAnyway}>
            {s.start}
          </Button>
        </p>
        <p className="note">{s.startNote}</p>
        <p className="boot-actions">
          <Button variant="quiet" onClick={() => void share()}>
            {s.share}
          </Button>{' '}
          <Button variant="quiet" onClick={() => void copy()}>
            {copied ? s.copied : s.copy}
          </Button>
        </p>
        <p className="note boot-detail" dir="ltr">
          {url}
        </p>
      </div>
    </div>
  );
}
